import {useSelector} from "react-redux";
import {useParams} from "react-router-dom";
import type {ICommandBarItemProps} from "@fluentui/react";

import type {RootState} from "../../../store";
import type MatLabError from "../../../types/MatLabError";

import {useAuth} from "../../../services/auth/hooks/useAuth";
import UserRoles from "../../../types/UserRoles";

import useConstructionCalculationsUIStateHook from "./useConstructionCalculationsUIStateHook";
import useConstructionCalculationsLoaderHook from "./useConstructionCalculationsLoaderHook";
import useConstructionCalculationsLifecycleHook from "./useConstructionCalculationsLifecycleHook";
import useCalculationErrorsHook from "./useCalculationErrorsHook";
import useConstructionCalculationStateChangeHook from "./useConstructionCalculationStateChangeHook";
import useMessagePageReloadHook from "./useMessagePageReloadHook";
import { useCommandBarItems } from "../components/commandBar/useCommandBarItems";
import type CalculationListItemDto from "../../../dto/calculations/CalculationListItemDto";
import type CalculationResultDto from "../../../dto/calculations/CalculationResultDto";
import type CalculationConstructionDetailsDto from "../../../dto/calculations/CalculationConstructionDetailsDto";
import type ConstructionAllDetailsGuiDto from "../../../dto/construction/ConstructionAllDetailsGuiDto";

/**
 * Orchestrator:
 * Orchestrator for following hooks:
 * - useConstructionCalculationsLoaderHook
 * - useConstructionCalculationsUIStateHook
 * - useConstructionCalculationsLifecycleHook
 * - useMessagePageReloadHook
 * - useCalculationErrorsHook
 * - useConstructionCalculationStateChangeHook
 */

const useConstructionCalculationsPageHookOrchestrator = (): {
  calculations: CalculationListItemDto[];
  selectedMastNo: string | undefined;
  selectedCalculationResults: CalculationResultDto | undefined;
  constructionDetails: CalculationConstructionDetailsDto | undefined;
  isAllowToTakeAction: boolean;
  sideMenuOpen: boolean;
  loading: boolean;
  confirmationModalOpen: boolean;
  construction: ConstructionAllDetailsGuiDto | undefined;
  commandBarItems: ICommandBarItemProps[];
  commandBarFarItems: ICommandBarItemProps[] | undefined;
  errors: MatLabError[] | undefined;
  loadPageData: (selectedCalculationId?: string | null) => Promise<void>;
  handleSideMenuOpenState: () => void;
  handleConfirm: () => Promise<void>;
  handleDismiss: () => void;
} => {
  const {user} = useAuth();
  
  const { calculationId } = useParams<{calculationId: string}>();

   // Data loading: fetch + dispatch to Redux
  const {loadPageData} = useConstructionCalculationsLoaderHook();

  // UI state: loading indicator + side panel toggle
  const {loading, setLoading, sideMenuOpen, handleSideMenuOpenState} =
    useConstructionCalculationsUIStateHook();

  // Lifecycle: initial load on mount, Redux cleanup on unmount
  useConstructionCalculationsLifecycleHook(loadPageData, setLoading, calculationId);

  // Real-time: reload page data when relevant SignalR messages arrive
  useMessagePageReloadHook(loadPageData, setLoading);

  // Redux selectors
  const {data: construction} = useSelector((state: RootState) => state.constructionNew);
  const {calculations, selectedMastNo, constructionDetails, selectedCalculationResults} = useSelector(
    (state: RootState) => state.constructionCalculationsNew,
  );

  // Error parsing: extract MatLab errors from calculation results
  const errors = useCalculationErrorsHook(selectedCalculationResults);

  // Accept / Unaccept state change modal
  const {
    handleConfirm,
    handleDismiss,
    handleOpen: handleConfirmModalOpenState,
    open: confirmationModalOpen,
  } = useConstructionCalculationStateChangeHook(construction ?? undefined, selectedCalculationResults);

  // Command bar button configuration
  const {commandBarItems, commandBarFarItems} = useCommandBarItems(
    construction ?? undefined,
    user?.isAllowedToTakeAction(),
    user?.isInRole([UserRoles.DesignEngineer]),
    selectedCalculationResults,
    errors !== undefined,
    handleConfirmModalOpenState,
    handleSideMenuOpenState,
  );

  return {
    isAllowToTakeAction: user?.isAllowedToTakeAction(),
    calculations,
    selectedMastNo,
    selectedCalculationResults,
    constructionDetails,
    construction: construction ?? undefined,
    sideMenuOpen,
    commandBarItems,
    commandBarFarItems,
    confirmationModalOpen,
    loading,
    errors,
    loadPageData,
    handleSideMenuOpenState,
    handleConfirm,
    handleDismiss,
  };
};

export default useConstructionCalculationsPageHookOrchestrator;
